import { useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'

interface Props {
  analyser: AnalyserNode | null
  active: boolean
  size?: number
  activeColor?: string
  idleColor?: string
}

export function VUMeter({ analyser, active, size = 120, activeColor = '#EF4444', idleColor = '#F97316' }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rafRef = useRef<number | null>(null)
  const bufRef = useRef<Uint8Array<ArrayBuffer> | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = size * dpr
    canvas.height = size * dpr
    ctx.scale(dpr, dpr)

    const cx = size / 2, cy = size / 2, R = size / 2 - 4, IR = R - 10

    function draw() {
      if (!ctx) return
      ctx.clearRect(0, 0, size, size)
      let level = 0
      if (active && analyser) {
        if (!bufRef.current) bufRef.current = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount))
        analyser.getByteFrequencyData(bufRef.current)
        const sum = bufRef.current.reduce((a, b) => a + b, 0)
        level = Math.min(1, sum / (bufRef.current.length * 128))
      }
      const color = active ? activeColor : idleColor
      const arcStart = -Math.PI / 2
      const arcEnd = arcStart + Math.PI * 2 * (active ? Math.max(0.08, level) : 1)

      ctx.beginPath()
      ctx.arc(cx, cy, (R + IR) / 2, 0, Math.PI * 2)
      ctx.strokeStyle = 'rgba(255,255,255,0.08)'
      ctx.lineWidth = R - IR
      ctx.stroke()

      ctx.beginPath()
      ctx.arc(cx, cy, (R + IR) / 2, arcStart, arcEnd)
      ctx.strokeStyle = color + (active ? 'ff' : '66')
      ctx.lineWidth = R - IR
      ctx.lineCap = 'round'
      ctx.stroke()

      if (active) {
        ctx.shadowColor = color
        ctx.shadowBlur = 12 + level * 16
        ctx.beginPath()
        ctx.arc(cx, cy, (R + IR) / 2, arcStart, arcEnd)
        ctx.stroke()
        ctx.shadowBlur = 0
      }
      rafRef.current = requestAnimationFrame(draw)
    }
    rafRef.current = requestAnimationFrame(draw)
    return () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current) }
  }, [analyser, active, size, activeColor, idleColor])

  const style: CSSProperties = { width: size, height: size, display: 'block' }
  return <canvas ref={canvasRef} style={style} />
}
